import { useEffect, useRef } from "react";
import * as THREE from "three";
import { FRAME_COLORS, FABRIC_COLORS, getDims } from "../lib/constants";

const frameColorHex = (id) => {
  const c = FRAME_COLORS.find((x) => x.id === id);
  if (!c) return "#F4F8F4";
  if (c.hex.startsWith("linear-gradient")) return "#005f6b";
  return c.hex;
};
const fabricColorHex = (id) =>
  FABRIC_COLORS.find((c) => c.id === id)?.hex || "#FFFFFF";

export default function Pergola3D({ config }) {
  const mountRef = useRef(null);
  const stateRef = useRef({});

  // One-time setup
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const w0 = mount.clientWidth;
    const h0 = mount.clientHeight;

    const scene = new THREE.Scene();

    // --- Sunset / golden hour sky ---
    const skyCanvas = document.createElement("canvas");
    skyCanvas.width = 2; skyCanvas.height = 1024;
    const sctx = skyCanvas.getContext("2d");
    const grad = sctx.createLinearGradient(0, 0, 0, 1024);
    grad.addColorStop(0, "#1f3851");
    grad.addColorStop(0.35, "#5e7691");
    grad.addColorStop(0.6, "#d8a87a");
    grad.addColorStop(0.78, "#f0c890");
    grad.addColorStop(1, "#2a1f1a");
    sctx.fillStyle = grad;
    sctx.fillRect(0, 0, 2, 1024);
    const skyTex = new THREE.CanvasTexture(skyCanvas);
    skyTex.colorSpace = THREE.SRGBColorSpace;
    scene.background = skyTex;
    scene.fog = new THREE.Fog(0xc8aa86, 26, 60);

    const camera = new THREE.PerspectiveCamera(34, w0 / h0, 0.1, 200);
    camera.position.set(7.5, 4.8, 10);
    camera.lookAt(0, 1.6, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(w0, h0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
    mount.appendChild(renderer.domElement);

    // --- Lights ---
    const ambient = new THREE.HemisphereLight(0xfbd9a6, 0x2a1f1a, 0.7);
    scene.add(ambient);
    const sun = new THREE.DirectionalLight(0xffd9a0, 2.4);
    sun.position.set(10, 8, 5);
    sun.castShadow = true;
    sun.shadow.mapSize.width = 2048;
    sun.shadow.mapSize.height = 2048;
    sun.shadow.camera.left = -14;
    sun.shadow.camera.right = 14;
    sun.shadow.camera.top = 14;
    sun.shadow.camera.bottom = -14;
    sun.shadow.bias = -0.00035;
    sun.shadow.radius = 4;
    scene.add(sun);
    const fill = new THREE.DirectionalLight(0x6f8eac, 0.6);
    fill.position.set(-8, 5, -4);
    scene.add(fill);
    const rim = new THREE.PointLight(0xffb878, 1.2, 25, 2);
    rim.position.set(-6, 2.2, -3);
    scene.add(rim);

    // --- Travertine terrace (cream marble pattern) ---
    const tCanvas = document.createElement("canvas");
    tCanvas.width = 512; tCanvas.height = 512;
    const tctx = tCanvas.getContext("2d");
    const baseGrad = tctx.createLinearGradient(0, 0, 512, 512);
    baseGrad.addColorStop(0, "#e8d8b8");
    baseGrad.addColorStop(0.5, "#d8c5a0");
    baseGrad.addColorStop(1, "#e8d8b8");
    tctx.fillStyle = baseGrad;
    tctx.fillRect(0, 0, 512, 512);
    tctx.strokeStyle = "rgba(140,110,80,0.18)";
    tctx.lineWidth = 1;
    for (let i = 0; i < 24; i++) {
      tctx.beginPath();
      tctx.moveTo(Math.random() * 512, 0);
      tctx.bezierCurveTo(
        Math.random() * 512, Math.random() * 512,
        Math.random() * 512, Math.random() * 512,
        Math.random() * 512, 512,
      );
      tctx.stroke();
    }
    tctx.strokeStyle = "rgba(120,100,75,0.55)";
    tctx.lineWidth = 3;
    for (let i = 0; i <= 2; i++) {
      tctx.beginPath();
      tctx.moveTo(i * 256, 0); tctx.lineTo(i * 256, 512); tctx.stroke();
      tctx.beginPath();
      tctx.moveTo(0, i * 256); tctx.lineTo(512, i * 256); tctx.stroke();
    }
    const groundTex = new THREE.CanvasTexture(tCanvas);
    groundTex.wrapS = groundTex.wrapT = THREE.RepeatWrapping;
    groundTex.repeat.set(6, 6);
    groundTex.colorSpace = THREE.SRGBColorSpace;
    groundTex.anisotropy = 8;
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(60, 60),
      new THREE.MeshStandardMaterial({ map: groundTex, roughness: 0.62, metalness: 0.05 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    // --- Luxury villa wall (warm sandstone) ---
    const wallCanvas = document.createElement("canvas");
    wallCanvas.width = 512; wallCanvas.height = 512;
    const wctx = wallCanvas.getContext("2d");
    wctx.fillStyle = "#cdb18c";
    wctx.fillRect(0, 0, 512, 512);
    wctx.fillStyle = "#a68a66";
    for (let row = 0; row < 8; row++) {
      const offset = (row % 2) * 32;
      for (let col = 0; col < 9; col++) {
        wctx.fillRect(col * 64 - offset, row * 64, 2, 64);
      }
      wctx.fillRect(0, row * 64, 512, 2);
    }
    for (let i = 0; i < 1500; i++) {
      wctx.fillStyle = `rgba(${100 + Math.random() * 80},${80 + Math.random() * 60},${50 + Math.random() * 50},${Math.random() * 0.22})`;
      wctx.fillRect(Math.random() * 512, Math.random() * 512, 2, 2);
    }
    const wallTex = new THREE.CanvasTexture(wallCanvas);
    wallTex.wrapS = wallTex.wrapT = THREE.RepeatWrapping;
    wallTex.repeat.set(3, 1.5);
    wallTex.colorSpace = THREE.SRGBColorSpace;
    wallTex.anisotropy = 8;
    const wall = new THREE.Mesh(
      new THREE.PlaneGeometry(32, 6),
      new THREE.MeshStandardMaterial({ map: wallTex, roughness: 0.92, metalness: 0.02 }),
    );
    wall.position.set(0, 3, -4);
    wall.receiveShadow = true;
    scene.add(wall);

    // Warm-glow windows
    const win1 = new THREE.Mesh(
      new THREE.PlaneGeometry(1.2, 2.4),
      new THREE.MeshStandardMaterial({ color: 0x2a3138, emissive: 0xffb878, emissiveIntensity: 0.6, roughness: 0.2, metalness: 0.4 }),
    );
    win1.position.set(-3.5, 1.8, -3.94);
    scene.add(win1);
    const win2 = new THREE.Mesh(
      new THREE.PlaneGeometry(2.6, 2.2),
      new THREE.MeshStandardMaterial({ color: 0x2a3138, emissive: 0xffb878, emissiveIntensity: 0.45, roughness: 0.2, metalness: 0.4 }),
    );
    win2.position.set(4, 1.7, -3.94);
    scene.add(win2);

    // Cypress silhouettes
    const cypressMat = new THREE.MeshStandardMaterial({ color: 0x2a3d28, roughness: 1, flatShading: true });
    [-9, -7.2, -5.5, 5.8, 7.5, 9.2].forEach((x) => {
      const tree = new THREE.Mesh(new THREE.ConeGeometry(0.45, 2.8 + Math.random() * 0.7, 8), cypressMat);
      tree.position.set(x, 1.4, -3.6);
      tree.castShadow = true;
      scene.add(tree);
    });

    // Lush hedge
    const hedgeMat = new THREE.MeshStandardMaterial({ color: 0x3e5a2c, roughness: 1, flatShading: true });
    for (let i = -5; i <= 5; i++) {
      const bush = new THREE.Mesh(new THREE.SphereGeometry(0.45 + Math.random() * 0.18, 8, 6), hedgeMat);
      bush.position.set(i * 1.0 + (Math.random() - 0.5) * 0.25, 0.32, 4.6);
      bush.castShadow = true;
      bush.receiveShadow = true;
      scene.add(bush);
    }

    // Premium ceramic planters with topiary
    const planterMat = new THREE.MeshStandardMaterial({ color: 0x161616, roughness: 0.45, metalness: 0.2 });
    const topiaryMat = new THREE.MeshStandardMaterial({ color: 0x3a5a2a, roughness: 0.95, flatShading: true });
    [-3.6, 3.6].forEach((x) => {
      const planter = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.22, 0.55, 16), planterMat);
      planter.position.set(x, 0.275, 1.9);
      planter.castShadow = true; planter.receiveShadow = true;
      scene.add(planter);
      const topiary = new THREE.Mesh(new THREE.SphereGeometry(0.38, 14, 10), topiaryMat);
      topiary.position.set(x, 0.94, 1.9);
      topiary.castShadow = true;
      scene.add(topiary);
    });

    // Marble coffee table + gold bowl
    const marbleMat = new THREE.MeshPhysicalMaterial({
      color: 0xefe3cf, roughness: 0.28, metalness: 0.05, clearcoat: 0.4,
    });
    const tableTop = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.045, 0.6), marbleMat);
    tableTop.position.set(0, 0.45, 1.3);
    tableTop.castShadow = true; tableTop.receiveShadow = true;
    scene.add(tableTop);
    const tableLeg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.11, 0.42, 12),
      new THREE.MeshStandardMaterial({ color: 0x1a1a1a, metalness: 0.7, roughness: 0.3 }),
    );
    tableLeg.position.set(0, 0.21, 1.3);
    tableLeg.castShadow = true;
    scene.add(tableLeg);
    const bowl = new THREE.Mesh(
      new THREE.SphereGeometry(0.11, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2.4),
      new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.8, roughness: 0.25 }),
    );
    bowl.position.set(0.15, 0.52, 1.3);
    bowl.castShadow = true;
    scene.add(bowl);

    // Pergola root group
    const root = new THREE.Group();
    scene.add(root);

    // Orbit state
    const orbit = {
      target: new THREE.Vector3(0, 1.6, 0),
      theta: Math.PI / 3.6,
      phi: Math.PI / 3.1,
      radius: 12,
      autoRotate: true,
      isDown: false,
      lastX: 0, lastY: 0,
    };
    const updateCamera = () => {
      camera.position.set(
        orbit.target.x + orbit.radius * Math.sin(orbit.phi) * Math.cos(orbit.theta),
        orbit.target.y + orbit.radius * Math.cos(orbit.phi),
        orbit.target.z + orbit.radius * Math.sin(orbit.phi) * Math.sin(orbit.theta),
      );
      camera.lookAt(orbit.target);
    };
    updateCamera();

    const dom = renderer.domElement;
    dom.style.cursor = "grab";
    const onDown = (e) => { orbit.isDown = true; orbit.autoRotate = false; orbit.lastX = e.clientX; orbit.lastY = e.clientY; dom.style.cursor = "grabbing"; };
    const onUp = () => { orbit.isDown = false; dom.style.cursor = "grab"; };
    const onMove = (e) => {
      if (!orbit.isDown) return;
      orbit.theta -= (e.clientX - orbit.lastX) * 0.005;
      orbit.phi = Math.max(0.25, Math.min(Math.PI / 2.05, orbit.phi - (e.clientY - orbit.lastY) * 0.005));
      orbit.lastX = e.clientX; orbit.lastY = e.clientY;
      updateCamera();
    };
    const onWheel = (e) => { e.preventDefault(); orbit.radius = Math.max(6, Math.min(18, orbit.radius + e.deltaY * 0.012)); updateCamera(); };
    dom.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointermove", onMove);
    dom.addEventListener("wheel", onWheel, { passive: false });

    const clock = new THREE.Clock();
    let raf;

    const animate = () => {
      const t = clock.getElapsedTime();
      if (orbit.autoRotate) { orbit.theta += 0.002; updateCamera(); }

      const cfg = stateRef.current.config;
      const fabricMesh = stateRef.current.fabricMesh;
      const ribGroup = stateRef.current.ribGroup;
      const stackGroup = stateRef.current.stackGroup;
      const p = stateRef.current.projection || 0;
      const ribCount = stateRef.current.ribCount || 0;
      if (cfg && fabricMesh && ribGroup && stackGroup) {
        const motorized = cfg.control === "somfy_io" || cfg.control === "electric";
        // Slow, smooth retraction (cycle ~16s) — eased sine
        const raw = motorized ? (Math.sin(t * 0.4) * 0.5 + 0.5) : 0;
        const progress = motorized ? raw * raw * (3 - 2 * raw) : 0; // smoothstep
        const extent = 1 - progress * 0.85;
        // PlaneGeometry projection axis is local Y (before rotation), so scale Y
        fabricMesh.scale.y = extent;
        fabricMesh.position.z = -p / 2 + (p * extent) / 2;

        // Move ribs along the projection
        ribGroup.children.forEach((rib, i) => {
          const baseZ = -p / 2 + ((i + 1) / (ribCount + 1)) * p;
          rib.position.z = -p / 2 + (baseZ + p / 2) * extent;
          rib.visible = progress < 0.95;
        });
        // Show "gathered" folds at wall side when retracting
        const stackVisible = progress > 0.05;
        stackGroup.visible = stackVisible;
        stackGroup.scale.y = 0.4 + progress * 1.3;
        stackGroup.children.forEach((fold, i) => {
          const offset = i * 0.045 * progress;
          fold.position.z = -p / 2 - offset;
        });
      }

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth, h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    stateRef.current = {
      scene, camera, renderer, root,
      cleanup: () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        dom.removeEventListener("pointerdown", onDown);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointermove", onMove);
        dom.removeEventListener("wheel", onWheel);
        renderer.dispose();
        if (dom.parentNode) dom.parentNode.removeChild(dom);
        scene.traverse((o) => {
          if (o.geometry) o.geometry.dispose?.();
          if (o.material) {
            if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
            else o.material.dispose?.();
          }
        });
      },
    };

    return () => stateRef.current.cleanup?.();
  }, []);

  // Rebuild pergola on config change
  useEffect(() => {
    const s = stateRef.current;
    if (!s || !s.root) return;
    s.config = config;

    // Clear root
    while (s.root.children.length > 0) {
      const c = s.root.children.pop();
      c.traverse?.((o) => {
        o.geometry?.dispose?.();
        if (o.material) { Array.isArray(o.material) ? o.material.forEach((m) => m.dispose?.()) : o.material.dispose?.(); }
      });
    }

    const dims = getDims(config);
    const W = dims.width / 100;        // metres
    const P = dims.projection / 100;
    const H = 2.5;
    const frameColor = new THREE.Color(frameColorHex(config.frame_color));
    const fabricColor = new THREE.Color(fabricColorHex(config.fabric_color));
    const isW96 = config.fabric_type === "w96";

    const buildUnit = (offsetX) => {
      const unit = new THREE.Group();
      unit.position.x = offsetX;

      const matFrame = new THREE.MeshStandardMaterial({
        color: frameColor, metalness: 0.35, roughness: 0.45,
      });

      // Wall-side support beam (mounted on building behind)
      const wallBeamGeo = new THREE.BoxGeometry(W + 0.2, 0.18, 0.14);
      const wallBeam = new THREE.Mesh(wallBeamGeo, matFrame);
      wallBeam.position.set(0, H, -P / 2);
      wallBeam.castShadow = true;
      unit.add(wallBeam);

      // Outer front beam (slightly lower for drainage slope)
      const frontBeam = new THREE.Mesh(wallBeamGeo, matFrame);
      frontBeam.position.set(0, H - 0.05, P / 2);
      frontBeam.castShadow = true;
      unit.add(frontBeam);

      // Side beams (projection)
      const sideBeamGeo = new THREE.BoxGeometry(0.14, 0.18, P);
      [-W / 2, W / 2].forEach((sx) => {
        const sb = new THREE.Mesh(sideBeamGeo, matFrame);
        sb.position.set(sx, H - 0.025, 0);
        sb.rotation.x = -0.018; // very slight slope
        sb.castShadow = true;
        unit.add(sb);
      });

      // Two front posts
      const postGeo = new THREE.BoxGeometry(0.12, H, 0.12);
      [-W / 2, W / 2].forEach((sx) => {
        const post = new THREE.Mesh(postGeo, matFrame);
        post.position.set(sx, H / 2 - 0.08, P / 2);
        post.castShadow = true;
        unit.add(post);
      });

      // Foot caps
      const footGeo = new THREE.BoxGeometry(0.2, 0.05, 0.2);
      [-W / 2, W / 2].forEach((sx) => {
        const f = new THREE.Mesh(footGeo, matFrame);
        f.position.set(sx, 0.025, P / 2);
        unit.add(f);
      });

      // ----- Fabric canopy (the main attraction) -----
      // PVC fabric: front-end facing slightly upward (toward wall), rendered double-sided
      const fabricMat = new THREE.MeshPhysicalMaterial({
        color: fabricColor,
        metalness: 0,
        roughness: isW96 ? 0.55 : 0.7,
        side: THREE.DoubleSide,
        sheen: 0.6,
        sheenColor: new THREE.Color(0xfff5e0),
        sheenRoughness: 0.6,
        transmission: isW96 ? 0.22 : 0,
        opacity: isW96 ? 0.92 : 1,
        transparent: isW96,
        clearcoat: 0.15,
        clearcoatRoughness: 0.65,
      });

      // Slight wave geometry for a real fabric feel
      const segX = 18, segZ = 18;
      const fabricGeo = new THREE.PlaneGeometry(W - 0.1, P - 0.05, segX, segZ);
      const pos = fabricGeo.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i), z = pos.getY(i);
        // Sag in the middle of each "panel" between ribs (subtle billow)
        const sag = Math.sin(((z + (P - 0.05) / 2) / (P - 0.05)) * Math.PI * 6) * 0.018;
        const cross = Math.cos((x / (W - 0.1)) * Math.PI) * 0.005;
        pos.setZ(i, sag + cross);
      }
      fabricGeo.computeVertexNormals();

      const fabric = new THREE.Mesh(fabricGeo, fabricMat);
      fabric.rotation.x = -Math.PI / 2;
      fabric.rotation.z = 0;
      fabric.position.set(0, H + 0.02, 0);
      fabric.castShadow = true;
      fabric.receiveShadow = true;
      unit.add(fabric);
      s.fabricMesh = fabric;
      s.projection = P;

      // ----- Cross ribs (small horizontal supports that travel with fabric) -----
      const ribCount = Math.max(3, Math.floor(P * 1.3));
      const ribGroup = new THREE.Group();
      ribGroup.position.set(0, H + 0.04, 0);
      const ribGeo = new THREE.BoxGeometry(W - 0.08, 0.025, 0.06);
      const ribMat = new THREE.MeshStandardMaterial({
        color: frameColor.clone().multiplyScalar(0.92),
        metalness: 0.5, roughness: 0.4,
      });
      for (let i = 0; i < ribCount; i++) {
        const rib = new THREE.Mesh(ribGeo, ribMat);
        rib.position.z = -P / 2 + ((i + 1) / (ribCount + 1)) * P;
        rib.castShadow = true;
        ribGroup.add(rib);
      }
      unit.add(ribGroup);
      s.ribGroup = ribGroup;
      s.ribCount = ribCount;

      // ----- Stacked folds at wall side (visible when retracted) -----
      const stackGroup = new THREE.Group();
      stackGroup.position.set(0, H + 0.04, 0);
      stackGroup.visible = false;
      const foldGeo = new THREE.BoxGeometry(W - 0.1, 0.05, 0.08);
      const foldMat = new THREE.MeshPhysicalMaterial({
        color: fabricColor, roughness: 0.7, sheen: 0.5,
      });
      for (let i = 0; i < 8; i++) {
        const fold = new THREE.Mesh(foldGeo, foldMat);
        fold.position.set(0, i * 0.05, -P / 2 - i * 0.04);
        fold.castShadow = true;
        stackGroup.add(fold);
      }
      unit.add(stackGroup);
      s.stackGroup = stackGroup;

      // ----- Accessories -----
      const ledSpot = config.accessories.includes("led_spot");
      const ledBox = config.accessories.includes("led_box");
      const windSensor = config.accessories.includes("wind_sensor");
      const phone = config.accessories.includes("smartphone");

      // LED Spot: a row along the wall beam
      if (ledSpot) {
        const spotCount = Math.max(4, Math.floor(W * 1.8));
        for (let i = 0; i < spotCount; i++) {
          const x = -W / 2 + (W / (spotCount + 1)) * (i + 1);
          const bulb = new THREE.Mesh(
            new THREE.SphereGeometry(0.045, 12, 12),
            new THREE.MeshStandardMaterial({ color: 0xfff4c2, emissive: 0xfff4c2, emissiveIntensity: 2.4 })
          );
          bulb.position.set(x, H - 0.03, -P / 2 + 0.08);
          unit.add(bulb);
          const pl = new THREE.PointLight(0xfff4c2, 0.45, 3.5, 2);
          pl.position.set(x, H - 0.2, 0);
          unit.add(pl);
        }
      }
      // LED Box: 4 boxes on the front posts/corners
      if (ledBox) {
        [-1, 1].forEach((sx) => {
          [-1, 1].forEach((sz) => {
            const box = new THREE.Mesh(
              new THREE.BoxGeometry(0.16, 0.1, 0.16),
              new THREE.MeshStandardMaterial({ color: 0xfff8d6, emissive: 0xfff8d6, emissiveIntensity: 1.8 })
            );
            box.position.set((W / 2 - 0.12) * sx, H - 0.05, (P / 2 - 0.12) * sz);
            unit.add(box);
            const pl = new THREE.PointLight(0xfff8d6, 0.35, 2.5, 2);
            pl.position.copy(box.position);
            unit.add(pl);
          });
        });
      }
      // Wind sensor on outer front corner
      if (windSensor) {
        const base = new THREE.Mesh(
          new THREE.CylinderGeometry(0.055, 0.055, 0.18, 16),
          new THREE.MeshStandardMaterial({ color: 0x1a1a1a, metalness: 0.6, roughness: 0.35 })
        );
        base.position.set(W / 2 - 0.18, H + 0.18, P / 2 - 0.1);
        unit.add(base);
        const cup = new THREE.Mesh(
          new THREE.SphereGeometry(0.05, 14, 14),
          new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.7, roughness: 0.3 })
        );
        cup.position.set(W / 2 - 0.18, H + 0.32, P / 2 - 0.1);
        unit.add(cup);
      }
      // Phone: small gold indicator on a post (smart-home pad)
      if (phone) {
        const pad = new THREE.Mesh(
          new THREE.BoxGeometry(0.08, 0.13, 0.02),
          new THREE.MeshStandardMaterial({ color: 0x1a1a1a, metalness: 0.4, roughness: 0.5 })
        );
        pad.position.set(-W / 2 + 0.07, 1.3, P / 2 + 0.07);
        unit.add(pad);
        const screen = new THREE.Mesh(
          new THREE.PlaneGeometry(0.05, 0.09),
          new THREE.MeshStandardMaterial({ color: 0xd4af37, emissive: 0xd4af37, emissiveIntensity: 0.7 })
        );
        screen.position.set(-W / 2 + 0.07, 1.3, P / 2 + 0.082);
        unit.add(screen);
      }

      return unit;
    };

    const multi = config.modular === "multiple";
    if (multi) {
      s.root.add(buildUnit(-W / 2 - 0.03));
      // For multi we keep the same fabric/ribs/stack refs from second unit (animation will only affect last set)
      const second = buildUnit(W / 2 + 0.03);
      s.root.add(second);
    } else {
      s.root.add(buildUnit(0));
    }
  }, [config]);

  return (
    <div
      data-testid="pergola-3d-canvas"
      ref={mountRef}
      className="w-full aspect-square bg-[#102429] border border-[#d4af37]/20 cursor-grab"
    />
  );
}
